import React, { useState, useEffect, useCallback } from 'react';
import { attendanceAPI, usersAPI, exportAPI, downloadFile } from '../services/api';
import {
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import moment from 'moment-timezone';
import UserProfileModal from '../components/UserProfileModal';
import UserModal from '../components/UserModal';
import Select from 'react-select';
import { toast } from 'react-toastify';

function AttendanceView() {
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // User profile modal state
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Create user modal state
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [prefillUser, setPrefillUser] = useState(null);
  const [userModalMode, setUserModalMode] = useState('create'); // 'create' | 'edit'

  // react-select styles (Selectize-like)
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 36,
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(59,130,246,0.1)' : 'none',
      '&:hover': { borderColor: '#9ca3af' }
    }),
    valueContainer: (base) => ({ ...base, padding: '2px 8px 2px 32px' }),
    indicatorsContainer: (base) => ({ ...base, height: 32 }),
    option: (base, state) => ({
      ...base,
      fontSize: 14,
      backgroundColor: state.isFocused ? '#f3f4f6' : 'white',
      color: '#111827'
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 })
  };

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    userId: '',
    department: '',
    managerId: '',
    unmappedOnly: false, // Filter for unmapped users
    startDate: moment().subtract(7, 'days').toDate(),
    endDate: new Date(),
    page: 1,
    limit: 20
  });

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadAttendanceLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        startDate: moment(filters.startDate).format('YYYY-MM-DD'),
        endDate: moment(filters.endDate).format('YYYY-MM-DD')
      };

      const response = await attendanceAPI.getAttendanceLogs(params);
      setAttendanceLogs(response.data.logs || []);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages,
        totalCount: response.data.totalCount
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadAttendanceLogs();
  }, [loadAttendanceLogs]);

  const loadInitialData = async () => {
    try {
      const [usersResponse, departmentsResponse] = await Promise.all([
        usersAPI.getUsers({ limit: 1000 }),
        usersAPI.getDepartmentsWithCount()
      ]);
      
      const allUsers = usersResponse.data.users || [];
      setUsers(allUsers);
      // Extract department names from the response
      const departmentNames = departmentsResponse.data?.departments?.map(dept => dept.name) || [];
      setDepartments(departmentNames);
      
      // Build manager options from actual user records so names show correctly.
      // Use each user's UUID as the value for filtering by manager.
      const managerMap = new Map();
      for (const u of allUsers) {
        if (!managerMap.has(u.id)) {
          const first = u.firstName || '';
          const last = u.lastName || '';
          managerMap.set(u.id, {
            id: u.id,
            firstName: first,
            lastName: last,
            fullName: `${first} ${last}`.trim() || (u.officialEmail || u.id)
          });
        }
      }
      setManagers(Array.from(managerMap.values()));
    } catch (err) {
      console.error('Error loading initial data:', err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      userId: '',
      department: '',
      managerId: '',
      unmappedOnly: false,
      startDate: moment().subtract(7, 'days').toDate(),
      endDate: new Date(),
      page: 1,
      limit: 20
    });
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const params = {
        startDate: moment(filters.startDate).format('YYYY-MM-DD'),
        endDate: moment(filters.endDate).format('YYYY-MM-DD'),
        userId: filters.userId,
        department: filters.department,
        managerId: filters.managerId
      };

      let response;
      let filename;

      if (format === 'csv') {
        response = await exportAPI.exportCSV(params);
        filename = `attendance_${moment().format('YYYY-MM-DD')}.csv`;
      } else if (format === 'excel') {
        response = await exportAPI.exportExcel(params);
        filename = `attendance_${moment().format('YYYY-MM-DD')}.xlsx`;
      }

      downloadFile(response.data, filename);
      toast.success(`Attendance data exported successfully as ${format.toUpperCase()}!`);
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleUserClick = (user) => {
    if (user && (user.firstName || user.lastName || user.officialEmail)) {
      setSelectedUser(user);
      setIsProfileModalOpen(true);
    }
  };
  const handleAddUserFromLog = (log) => {
    // If a user already exists with this UID, open in edit mode instead
    const existing = users.find(u => u.uidNumber && log.userId && String(u.uidNumber) === String(log.userId));
    if (existing) {
      setPrefillUser(existing);
      setUserModalMode('edit');
      setIsCreateUserModalOpen(true);
      return;
    }

    // Otherwise create mode: prefill UID and department
    const prefill = {
      uidNumber: log.userId || '',
      Department: log.user?.Department || '',
      firstName: '',
      lastName: '',
      officialEmail: ''
    };
    setPrefillUser(prefill);
    setUserModalMode('create');
    setIsCreateUserModalOpen(true);
  };

  const handleCreateUserClose = () => {
    setIsCreateUserModalOpen(false);
    setPrefillUser(null);
  };

  const handleCreateUserSave = async (data) => {
    try {
      if (userModalMode === 'edit' && prefillUser?.id) {
        await usersAPI.updateUser(prefillUser.id, data);
      } else {
        await usersAPI.createUser(data);
      }
      handleCreateUserClose();
      await loadInitialData();
      await loadAttendanceLogs();
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to save user';
      const details = err.response?.data?.details || null;
      const fields = err.response?.data?.fields || null;
      const code = err.response?.data?.code || null;
      const subordinates = err.response?.data?.subordinates || null;
      throw { message, details, fields, code, subordinates };
    }
  };

  const handleProfileModalClose = () => {
    setIsProfileModalOpen(false);
    setSelectedUser(null);
  };

  return (
    <div className="space-y-6">
      {/* Header - Modern Gradient Design */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 rounded-2xl shadow-2xl p-8 text-white">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <div className="h-16 w-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center border-2 border-white/40 shadow-2xl ring-4 ring-white/10 mr-5">
              <ClockIcon className="h-10 w-10 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold drop-shadow-lg">Attendance Logs</h1>
              <p className="text-white/90 mt-1 text-lg font-medium">
                {pagination.totalCount} records • View and manage attendance
              </p>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => handleExport('csv')}
              disabled={exporting}
              className="group inline-flex items-center px-5 py-3 border-2 border-white/30 text-sm font-semibold rounded-xl text-white bg-white/10 backdrop-blur-md hover:bg-white/20 focus:outline-none focus:ring-4 focus:ring-white/30 transition-all duration-200 shadow-xl hover:scale-105 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-5 w-5 mr-2 group-hover:animate-bounce" />
              Export CSV
            </button>
            <button
              onClick={() => handleExport('excel')}
              disabled={exporting}
              className="group inline-flex items-center px-5 py-3 border-2 border-transparent text-sm font-semibold rounded-xl text-emerald-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-white/50 transition-all duration-200 shadow-xl hover:scale-105 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-5 w-5 mr-2 group-hover:animate-bounce" />
              Export Excel
            </button>
          </div>
        </div>
      </div>

      {/* Filters - Modern Design */}
      <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b-2 border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <MagnifyingGlassIcon className="h-6 w-6 text-gray-400 mr-3" />
              <h3 className="text-lg font-bold text-gray-900">Search & Filters</h3>
            </div>
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-4 py-2 text-sm font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors duration-200 border border-teal-200"
            >
              Clear all
            </button>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

          {/* User or UID Search */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {filters.unmappedOnly ? 'Search Unmapped UID' : 'User'}
            </label>
            {filters.unmappedOnly ? (
              <>
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-9 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Enter UID number (e.g., 24747)"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </>
            ) : (
              <>
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-9 text-gray-400 pointer-events-none" />
                <Select
                  classNamePrefix="select"
                  isClearable
                  placeholder="Search users by name or email"
                  value={
                    filters.userId
                      ? {
                          value: filters.userId,
                          label: `${(users.find(u => u.id === filters.userId)?.firstName || '')} ${(users.find(u => u.id === filters.userId)?.lastName || '')}`.trim() || (users.find(u => u.id === filters.userId)?.officialEmail || 'Selected User')
                        }
                      : null
                  }
                  onChange={(opt) => handleFilterChange('userId', opt ? opt.value : '')}
                  options={[
                    { value: '', label: 'All Users' },
                    ...users.map(u => ({
                      value: u.id,
                      label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.officialEmail,
                      email: u.officialEmail
                    }))
                  ]}
                  styles={selectStyles}
                  filterOption={(option, raw) => {
                    const q = raw.toLowerCase();
                    const label = String(option.label || '').toLowerCase();
                    const email = String(option.data?.email || '').toLowerCase();
                    return label.includes(q) || email.includes(q);
                  }}
                />
              </>
            )}
          </div>

          {/* Department */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
            <Select
              classNamePrefix="select"
              isClearable
              placeholder="All Departments"
              value={filters.department ? { value: filters.department, label: filters.department } : null}
              onChange={(opt) => handleFilterChange('department', opt ? opt.value : '')}
              options={[{ value: '', label: 'All Departments' }, ...departments.map(d => ({ value: d, label: d }))]}
              styles={selectStyles}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPlacement="auto"
              menuPosition="fixed"
            />
          </div>

          {/* Manager */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Manager</label>
            <Select
              classNamePrefix="select"
              isClearable
              placeholder="All Managers"
              value={filters.managerId ? { value: filters.managerId, label: managers.find(m => m.id === filters.managerId)?.fullName || 'Manager' } : null}
              onChange={(opt) => handleFilterChange('managerId', opt ? opt.value : '')}
              options={[{ value: '', label: 'All Managers' }, ...managers.map(m => ({ value: m.id, label: m.fullName }))]}
              styles={selectStyles}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPlacement="auto"
              menuPosition="fixed"
            />
          </div>

          {/* Date Range */}
          <div className="md:col-span-2 lg:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <div className="flex space-x-2">
              <DatePicker
                selected={filters.startDate}
                onChange={(date) => handleFilterChange('startDate', date)}
                selectsStart
                startDate={filters.startDate}
                endDate={filters.endDate}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholderText="Start date"
              />
              <DatePicker
                selected={filters.endDate}
                onChange={(date) => handleFilterChange('endDate', date)}
                selectsEnd
                startDate={filters.startDate}
                endDate={filters.endDate}
                minDate={filters.startDate}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholderText="End date"
              />
            </div>
          </div>
        </div>

        {/* Unmapped Users Filter */}
        <div className="mt-4 flex items-center">
          <input
            id="unmappedOnly"
            type="checkbox"
            checked={filters.unmappedOnly}
            onChange={(e) => {
              handleFilterChange('unmappedOnly', e.target.checked);
              if (e.target.checked) {
                handleFilterChange('userId', '');
                handleFilterChange('department', '');
                handleFilterChange('managerId', '');
              }
            }}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <label htmlFor="unmappedOnly" className="ml-2 block text-sm text-gray-900">
            Show only unmapped users (UID without user profile)
          </label>
          {filters.unmappedOnly && (
            <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Unmapped filter active
            </span>
          )}
        </div>
        </div>
      </div>

      {/* Results - Modern Design */}
      <div className="bg-white rounded-xl shadow-xl border-2 border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b-2 border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-md mr-3">
                <ClockIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Attendance Records
                </h3>
                <p className="text-xs text-gray-500">{pagination.totalCount} total records</p>
              </div>
            </div>
          </div>
        </div>
        <div className="p-6">

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner"></div>
              <span className="ml-2 text-gray-600">Loading attendance logs...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-600 mb-2">Error: {error}</div>
              <button
                onClick={loadAttendanceLogs}
                className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm hover:bg-primary-700"
              >
                Retry
              </button>
            </div>
          ) : attendanceLogs.length === 0 ? (
            <div className="text-center py-12">
              <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No attendance records found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your filters or date range.
              </p>
            </div>
          ) : (
            <>
              {/* Table - Modern Design */}
              <div className="overflow-x-auto max-h-[60vh] overflow-y-auto rounded-xl border-2 border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-teal-50 to-cyan-50 sticky top-0 z-10 shadow-md">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-teal-900 uppercase tracking-wider border-b-2 border-teal-200">
                        Employee
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-teal-900 uppercase tracking-wider border-b-2 border-teal-200">
                        Department
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-teal-900 uppercase tracking-wider border-b-2 border-teal-200">
                        Log Time
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-teal-900 uppercase tracking-wider border-b-2 border-teal-200">
                        Type
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-teal-900 uppercase tracking-wider border-b-2 border-teal-200">
                        Device
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-teal-900 uppercase tracking-wider border-b-2 border-teal-200">
                        Input Type
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {attendanceLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gradient-to-r hover:from-teal-50/50 hover:to-cyan-50/50 transition-all duration-200">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shadow-md ring-2 ring-white">
                                <span className="text-sm font-bold text-white">
                                  {log.user ? 
                                    `${log.user.firstName?.[0] || ''}${log.user.lastName?.[0] || ''}` : 
                                    '??'
                                  }
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              {log.user ? (
                                <>
                                  <div className="text-sm font-medium">
                                    <button
                                      onClick={() => handleUserClick(log.user)}
                                      className="text-primary-600 hover:text-primary-800 hover:underline focus:outline-none"
                                    >
                                      {`${log.user.firstName || ''} ${log.user.lastName || ''}`.trim()}
                                    </button>
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {log.user?.officialEmail}
                                  </div>
                                </>
                              ) : (
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{log.userId || 'Unknown UID'}</div>
                                  <div className="flex items-center space-x-2 mt-1">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-sm">⚠️ Unmapped</span>
                                    <button
                                      onClick={() => handleAddUserFromLog(log)}
                                      className="inline-flex items-center px-2 py-1 text-xs font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-md transition-colors duration-200"
                                    >
                                      + Add user
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {log.user?.Department ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                              {log.user.Department}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-500">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {moment(log.logTime).tz('Asia/Kolkata').format('MMM DD, YYYY HH:mm:ss')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1.5 text-xs font-bold rounded-lg shadow-sm ${
                            log.punchLogType === 'Check In' 
                              ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' 
                              : log.punchLogType === 'Check Out'
                              ? 'bg-gradient-to-r from-red-500 to-rose-600 text-white'
                              : 'bg-gradient-to-r from-gray-400 to-gray-500 text-white'
                          }`}>
                            {log.punchLogType === 'Check In' ? '🟢 Check In' : 
                             log.punchLogType === 'Check Out' ? '🔴 Check Out' :
                             log.punchLogType || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {log.deviceNumber || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {log.inputType || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4">
                  <div className="flex flex-1 justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={pagination.currentPage === 1}
                      className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing{' '}
                        <span className="font-medium">
                          {(pagination.currentPage - 1) * filters.limit + 1}
                        </span>{' '}
                        to{' '}
                        <span className="font-medium">
                          {Math.min(pagination.currentPage * filters.limit, pagination.totalCount)}
                        </span>{' '}
                        of{' '}
                        <span className="font-medium">{pagination.totalCount}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                        <button
                          onClick={() => handlePageChange(pagination.currentPage - 1)}
                          disabled={pagination.currentPage === 1}
                          className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        
                        {/* Page numbers */}
                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                          const pageNum = Math.max(1, pagination.currentPage - 2) + i;
                          if (pageNum > pagination.totalPages) return null;
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                pageNum === pagination.currentPage
                                  ? 'z-10 bg-primary-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
                                  : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => handlePageChange(pagination.currentPage + 1)}
                          disabled={pagination.currentPage === pagination.totalPages}
                          className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={isProfileModalOpen}
        onClose={handleProfileModalClose}
        user={selectedUser}
      />

      {/* Create User Modal (opened when log has no user details) */}
      <UserModal
        isOpen={isCreateUserModalOpen}
        onClose={handleCreateUserClose}
        onSave={handleCreateUserSave}
        user={prefillUser}
        mode={userModalMode}
        departments={departments}
        workingPolicies={[]}
        users={users}
      />
    </div>
  );
}

export default AttendanceView;
